import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { WagmiProvider } from 'wagmi';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
// CRITICAL: register Sei Global Wallet via EIP-6963
import '@sei-js/sei-global-wallet/eip6963';

import { wagmiConfig } from '@/config/wagmi';
import { WalletProvider } from '@/contexts/WalletContext';
import { persistRefId } from '@/shared/services/auth.service';
import '@fontsource/lilita-one';
import './index.css';
import App from './App.tsx';

// Persist referral ID from URL on app init
persistRefId();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      staleTime: 0,
    },
  },
});

// Update viewport height on mobile when toolbar shows/hides
function updateViewportHeight() {
  // Get the actual viewport height
  const vh = window.innerHeight;
  // Set CSS variable
  document.documentElement.style.setProperty('--app-height', `${vh}px`);
}

// Initialize on load
updateViewportHeight();

// Update on resize (when Safari toolbar hides/shows)
window.addEventListener('resize', updateViewportHeight);

// Update on orientation change
window.addEventListener('orientationchange', updateViewportHeight);

// Use visualViewport API for better mobile support (iOS Safari)
if (window.visualViewport) {
  window.visualViewport.addEventListener('resize', updateViewportHeight);
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <WagmiProvider config={wagmiConfig}>
      <QueryClientProvider client={queryClient}>
        <WalletProvider>
          <App />
        </WalletProvider>
      </QueryClientProvider>
    </WagmiProvider>
  </StrictMode>
);
